import { EcommerceBaseItem, EcommercePurchasePayload } from '@qbe/qal/src/types';
import { analyticsConstant, AnalyticsTransactionType, GoalEvents } from 'types/analytics';
import { PaymentFrequencyOptions } from 'types/payments';

import { PolicyResponse } from '../../types/policy';
import { getAnnualRevenue, getMonthlyRevenue } from './getSlices/getEcommerce';

const GOAL = { eventCategory: 'goal' };
const { NOT_SET } = analyticsConstant;

type ErrorType = {
  question?: string;
  message?: string;
  code?: string;
  errorCategory?: string;
  covers?: string;
  technicalErrorType?: string;
  status?: string;
  error?: string;
  data?: object;
  [k: string]: unknown;
};

export const qalFormatBusinessError = (error: ErrorType) => ({
  errorCode: error.code || NOT_SET,
  questionId: error.question || NOT_SET,
  message: error.message || NOT_SET,
  errorType: error.question || NOT_SET,
  data: error.data,
});

export const qalFormatSoftBusinessError = (error: ErrorType) => ({
  questionId: error.covers?.toString() || NOT_SET, // Question ID
  errorType: error.covers?.toString() || NOT_SET,
  message: error.message || error.error || NOT_SET,
  data: error.data,
});

export const qalFormatTechnicalError = (error: ErrorType) => ({
  errorCode: error.code || NOT_SET,
  errorType: error.technicalErrorType || error.status || NOT_SET,
  message: error.message || error.error || NOT_SET,
  data: error.data,
});

export const formatTiming = (
  timingValue: string,
  timingCategory: string,
  timingVar: string,
  timingLabel: string
) => ({
  timingValue,
  timingCategory,
  timingVar,
  timingLabel,
});

export const retrieveQuote = (retrieveQuoteStatus: string) => ({
  event: GoalEvents.RetrieveQuote,
  eventAction: 'retrieve quote',
  ...GOAL,
  eventLabel: retrieveQuoteStatus,
  data: {},
});

export const renewalPaymentStart = (policyNumber: string) => ({
  event: GoalEvents.TransactionStart,
  ...GOAL,
  eventAction: 'transaction start',
  eventLabel: policyNumber,
  data: {
    transactionType: AnalyticsTransactionType.renewalPayment,
  },
});

export const renewalPaymentTransactionComplete = (
  paymentReference: string,
  policyNumber: string,
  paymentAmount: number
) => ({
  event: GoalEvents.TransactionComplete,
  ...GOAL,
  eventAction: 'transaction complete',
  eventLabel: paymentReference,
  data: {
    transactionType: AnalyticsTransactionType.renewalPayment,
    paymentAmount,
    policyNumber,
    paymentReference,
    cm_payment_amount: paymentAmount,
  },
});

// UA renewal payment ecommerce
export const renewalPaymentComplete = (
  paymentReference: string,
  policyNumber: string,
  paymentAmount: number
) => ({
  eventAction: 'payment complete',
  eventCategory: 'goal',
  eventLabel: paymentReference,
  data: {
    transactionType: AnalyticsTransactionType.renewalPayment,
    paymentAmount,
    policyNumber,
  },
  ecommerce: {
    purchase: {
      actionField: {
        id: policyNumber,
        affiliation: window.qbeTrack?.meta?.appPartner,
        revenue: paymentAmount,
      },
      product: [
        {
          brand: window.qbeTrack?.meta?.appPartner,
          category: 'digiSME renewal payment',
          variant: 'OneOff',
          quantity: 1,
          name: 'digiSME renewal payment',
          price: paymentAmount,
        },
      ],
    },
  },
});

// GA4 renewal payment ecommerce
export const renewalPaymentCompleteGA4 = (
  paymentReference: string,
  policyNumber: string,
  paymentAmount: number
) => ({
  eventCategory: 'goal',
  eventAction: 'payment complete',
  eventLabel: paymentReference,
  data: {
    transactionType: AnalyticsTransactionType.renewalPayment,
    paymentAmount,
    policyNumber,
  },
  ecommerce: {
    purchase: {
      actionField: {
        id: policyNumber,
        affiliation: window.qbeTrack?.meta?.appPartner,
        revenue: paymentAmount,
      },
      product: [
        {
          brand: window.qbeTrack?.meta?.appPartner,
          category: 'digiSME renewal payment',
          variant: 'OneOff',
          quantity: 1,
          name: 'digiSME renewal payment',
          price: paymentAmount,
        },
      ],
    },
  },
});

// QAL Format
export const qalRenewalPaymentStart = (policyNumber: string) => ({
  eventLabel: policyNumber,
  data: {
    transactionType: AnalyticsTransactionType.renewalPayment,
  },
});

export const qalRenewalPaymentComplete = (
  paymentReference: string,
  policyNumber: string,
  paymentAmount: number
) => ({
  eventLabel: paymentReference,
  data: {
    paymentAmount,
    policyNumber,
    paymentReference,
    cm_payment_amount: paymentAmount,
    transactionType: AnalyticsTransactionType.renewalPayment,
  },
});

export const qalRenewalPaymentEcommerece = (
  paymentReference: string,
  policyNumber: string,
  paymentAmount: number
) => ({
  eventLabel: policyNumber,
  ecommerce: {
    currency: 'AUD',
    transaction_id: paymentReference, // payment reference number
    coupon: '',
    value: paymentAmount, // Premium
    items: [
      {
        item_name: 'digiSME renewal payment', // Cover Type
        item_id: 'digiSME renewal payment',
        affiliation: window.qbeTrack?.meta?.appPartner || 'QBE Direct', // Optional
        coupon: '', // Optional
        discount: 0, // discount
        index: 0, // Optional,
        item_brand: 'QBE', // Optional
        item_category: '',
        item_category2: '',
        item_category3: '',
        item_category4: '',
        item_category5: '',
        item_list_id: '',
        item_list_name: '',
        item_variant: '',
        location_id: '',
        price: paymentAmount,
        quantity: 1,
      },
    ],
  },
  data: {
    transactionType: AnalyticsTransactionType.renewalPayment,
  },
});

export const qalECommerceGA4 = (
  policyNumber: string,
  premiums: PolicyResponse,
  paymentFrequency: string,
  industry: string
): EcommercePurchasePayload => ({
  transaction_id: policyNumber,
  currency: 'AUD',
  value: premiums?.policy.policyPremium?.transactionPremium?.premium ?? 0,
  items:
    paymentFrequency === PaymentFrequencyOptions.OneOff
      ? (getAnnualRevenue(policyNumber, premiums, industry, 'GA4') as EcommerceBaseItem[])
      : (getMonthlyRevenue(policyNumber, premiums, industry, 'GA4') as EcommerceBaseItem[]),
});
